import { useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { Chess, type Square } from 'chess.js'

/**
 * THE BOARD — owner, 2026-10-19: 西洋棋, 讓兩個使用者可以互相對弈.
 *
 * It draws a position and lets the viewer move their own pieces, by TAP (a piece, then a square) or by DRAG; the
 * legal squares come from the same rules the service referees with (chess.js), so the board never offers a move the
 * game would refuse — and the service still decides. The viewer's side is at the bottom.
 *
 * Pieces are the Unicode chess glyphs, the SOLID set for both colours (one silhouette, filled light or dark), with
 * VS15 (U+FE0E) so a phone draws text and not an emoji pawn.
 */

export type BoardMove = { from: string; to: string; promotion?: string }

const GLYPH: Record<string, string> = { k: '♚', q: '♛', r: '♜', b: '♝', n: '♞', p: '♟' }
const NAME: Record<string, string> = { k: 'king', q: 'queen', r: 'rook', b: 'bishop', n: 'knight', p: 'pawn' }
const FILES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h']
const PROMOTIONS = ['q', 'r', 'b', 'n'] as const

function squaresFor(orientation: 'w' | 'b'): string[] {
  const out: string[] = []
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const file = orientation === 'w' ? FILES[col]! : FILES[7 - col]!
      const rank = orientation === 'w' ? 8 - row : row + 1
      out.push(`${file}${rank}`)
    }
  }
  return out
}

/** The centre of a square in the board's own 8×8 units (for the hint arrow). */
function centre(sq: string, orientation: 'w' | 'b'): [number, number] {
  const f = FILES.indexOf(sq[0]!)
  const r = Number(sq[1]) - 1
  return orientation === 'w' ? [f + 0.5, 7 - r + 0.5] : [7 - f + 0.5, r + 0.5]
}

export function ChessBoard({
  fen,
  orientation,
  movable,
  last,
  check,
  hint,
  onMove,
}: {
  fen: string
  orientation: 'w' | 'b'
  /** The side the viewer may move now; null while it is not their move (or the game is over). */
  movable: 'w' | 'b' | null
  last: { from: string; to: string } | null
  check: boolean
  /** Your agent's suggestion, drawn as an arrow. */
  hint: { from: string; to: string } | null
  onMove: (m: BoardMove) => void
}) {
  const game = useMemo(() => {
    try {
      return new Chess(fen)
    } catch {
      return new Chess()
    }
  }, [fen])
  const [selected, setSelected] = useState<string | null>(null)
  const [promo, setPromo] = useState<{ from: string; to: string } | null>(null)
  const [drag, setDrag] = useState<{ from: string; x: number; y: number } | null>(null)
  const boardRef = useRef<HTMLDivElement>(null)
  const pressed = useRef<{ from: string; x: number; y: number; moved: boolean } | null>(null)
  // A drag ends in a click on the square it began on; that click is the drag's, not a tap.
  const dragged = useRef(false)

  // A new position (a move landed, or the game was re-read) clears whatever was half-chosen.
  const [seenFen, setSeenFen] = useState(fen)
  if (seenFen !== fen) {
    setSeenFen(fen)
    setSelected(null)
    setPromo(null)
    setDrag(null)
  }

  const mine = (sq: string) => {
    const p = game.get(sq as Square)
    return !!p && p.color === movable
  }
  const targets = useMemo(() => {
    if (!selected || !movable) return new Map<string, boolean>()
    const m = new Map<string, boolean>()
    for (const mv of game.moves({ square: selected as Square, verbose: true })) {
      m.set(mv.to, !!mv.captured || mv.flags.includes('e'))
    }
    return m
  }, [game, selected, movable])
  const kingInCheck = useMemo(() => {
    if (!check) return null
    const turn = game.turn()
    for (const row of game.board()) for (const p of row) if (p && p.type === 'k' && p.color === turn) return p.square
    return null
  }, [game, check])

  const tryMove = (from: string, to: string) => {
    const legal = game.moves({ square: from as Square, verbose: true }).filter((m) => m.to === to)
    if (!legal.length) return false
    if (legal.some((m) => m.promotion)) {
      setPromo({ from, to })
      return true
    }
    setSelected(null)
    onMove({ from, to })
    return true
  }

  const squareAt = (clientX: number, clientY: number): string | null => {
    const el = boardRef.current
    if (!el) return null
    const r = el.getBoundingClientRect()
    const col = Math.floor(((clientX - r.left) / r.width) * 8)
    const row = Math.floor(((clientY - r.top) / r.height) * 8)
    if (col < 0 || col > 7 || row < 0 || row > 7) return null
    return squaresFor(orientation)[row * 8 + col] ?? null
  }

  const onDown = (sq: string, e: ReactPointerEvent<HTMLButtonElement>) => {
    // Every click follows a press: a flag a drag left (its click landed elsewhere, or nowhere) ends here.
    dragged.current = false
    if (promo) return
    if (mine(sq)) {
      pressed.current = { from: sq, x: e.clientX, y: e.clientY, moved: false }
      try {
        boardRef.current?.setPointerCapture(e.pointerId)
      } catch {
        /* capture is a nicety: without it the drag ends where the pointer leaves */
      }
    }
  }
  const onMoveOver = (e: ReactPointerEvent<HTMLDivElement>) => {
    const p = pressed.current
    if (!p) return
    if (!p.moved && Math.hypot(e.clientX - p.x, e.clientY - p.y) < 6) return
    p.moved = true
    if (selected !== p.from) setSelected(p.from)
    setDrag({ from: p.from, x: e.clientX, y: e.clientY })
  }
  const onUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    const p = pressed.current
    pressed.current = null
    setDrag(null)
    if (!p || !p.moved) return
    dragged.current = true
    const to = squareAt(e.clientX, e.clientY)
    if (to && to !== p.from) tryMove(p.from, to)
  }

  const onTap = (sq: string) => {
    if (dragged.current) {
      dragged.current = false
      return
    }
    if (!movable || promo) return
    if (selected && sq !== selected && targets.has(sq)) {
      tryMove(selected, sq)
      return
    }
    setSelected(mine(sq) && sq !== selected ? sq : null)
  }

  const squares = squaresFor(orientation)
  const dragRect = boardRef.current?.getBoundingClientRect()
  const arrow = hint ? [centre(hint.from, orientation), centre(hint.to, orientation)] : null

  return (
    <div className="cb-wrap">
      <div
        ref={boardRef}
        className={`cb-board${movable ? ' cb-live' : ''}`}
        role="grid"
        aria-label="Chess board"
        onPointerMove={onMoveOver}
        onPointerUp={onUp}
        onPointerCancel={() => {
          pressed.current = null
          setDrag(null)
        }}
      >
        {squares.map((sq, i) => {
          const piece = game.get(sq as Square)
          const dark = (FILES.indexOf(sq[0]!) + Number(sq[1])) % 2 === 0
          const row = Math.floor(i / 8)
          const col = i % 8
          const cls = [
            'cb-sq',
            dark ? 'cb-dark' : 'cb-light',
            last && (sq === last.from || sq === last.to) ? 'cb-last' : '',
            sq === selected ? 'cb-sel' : '',
            sq === kingInCheck ? 'cb-check' : '',
            targets.has(sq) ? (targets.get(sq) ? 'cb-take' : 'cb-dot') : '',
          ]
            .filter(Boolean)
            .join(' ')
          const label = piece ? `${sq}, ${piece.color === 'w' ? 'white' : 'black'} ${NAME[piece.type]}` : sq
          return (
            <button
              key={sq}
              type="button"
              className={cls}
              aria-label={label}
              aria-pressed={sq === selected}
              onPointerDown={(e) => onDown(sq, e)}
              onClick={() => onTap(sq)}
            >
              {col === 0 && <span className="cb-rank">{sq[1]}</span>}
              {row === 7 && <span className="cb-file">{sq[0]}</span>}
              {piece && (
                <span
                  className={`cb-piece cb-${piece.color}${drag?.from === sq ? ' cb-lifted' : ''}`}
                  aria-hidden="true"
                >
                  {GLYPH[piece.type]}
                  {'︎'}
                </span>
              )}
            </button>
          )
        })}
        {arrow && (
          <svg className="cb-arrow" viewBox="0 0 8 8" aria-hidden="true">
            <defs>
              <marker id="cb-head" viewBox="0 0 4 4" refX="2" refY="2" markerWidth="2.6" markerHeight="2.6" orient="auto">
                <path d="M0,0 L4,2 L0,4 Z" />
              </marker>
            </defs>
            <line
              x1={arrow[0]![0]}
              y1={arrow[0]![1]}
              x2={arrow[1]![0] - (arrow[1]![0] - arrow[0]![0]) * 0.18}
              y2={arrow[1]![1] - (arrow[1]![1] - arrow[0]![1]) * 0.18}
              markerEnd="url(#cb-head)"
            />
          </svg>
        )}
        {drag && dragRect && (
          <span
            className={`cb-piece cb-${game.get(drag.from as Square)?.color ?? 'w'} cb-ghost`}
            style={{
              left: drag.x - dragRect.left,
              top: drag.y - dragRect.top,
              fontSize: dragRect.width / 8 * 0.82,
            }}
            aria-hidden="true"
          >
            {GLYPH[game.get(drag.from as Square)?.type ?? 'p']}
            {'︎'}
          </span>
        )}
        {promo && (
          <div className="cb-promo" role="dialog" aria-label="Promote to">
            {PROMOTIONS.map((t) => (
              <button
                key={t}
                type="button"
                className={`cb-promo-btn cb-piece cb-${movable ?? 'w'}`}
                aria-label={`Promote to ${NAME[t]}`}
                onClick={() => {
                  const m = { ...promo, promotion: t }
                  setPromo(null)
                  setSelected(null)
                  onMove(m)
                }}
              >
                {GLYPH[t]}
                {'︎'}
              </button>
            ))}
            <button type="button" className="cb-promo-x" onClick={() => setPromo(null)} aria-label="Cancel">
              ×
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
